// components/ComboDisplay/ComboDisplay.tsx

import React, { useContext, useEffect } from 'react';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import KeyPressGroup from './KeyPressGroup';
import { GameLog } from '../../../Logs/LogsDisplay';
import './ComboDisplay.css';
import { processLogsToTimelineEvents, TimelineEvent } from './utils';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StackTimeoutGroup from './Events/TimeOutEvents/StackTimeoutGroup';
import TakeDownGroupComponent from './Events/TakeDownEvents/TakeDownGroupComponent';
import KeyPressInterruptEvent from './Events/KeyPressInterruptEvent';

interface ComboDisplayProps {
  logs: GameLog[];
  championName: string;
  targetChampionName?: string;
  targetMaxHealth: number;
  showDetails: boolean;
  showStackingEvents: boolean;
  showStatEvents: boolean;
  showCCEvents: boolean;
}

const ComboDisplay: React.FC<ComboDisplayProps> = ({
  logs,
  championName,
  targetChampionName,
  targetMaxHealth,
  showDetails,
  showStackingEvents,
  showStatEvents,
  showCCEvents,
}) => {
  const { championsAssets, fetchAndSetChampionAssets } = useContext(GameAssetsContext);

  useEffect(() => {
    if (!championsAssets[championName]) {
      fetchAndSetChampionAssets(championName);
    }
  }, [championName, championsAssets, fetchAndSetChampionAssets]);

  if (!championsAssets[championName]) {
    return <div>Loading...</div>;
  }

  const { spellImageUrls, autoAttackImageUrl } = championsAssets[championName];

  // Generate the timeline events
  const timelineEvents: TimelineEvent[] = processLogsToTimelineEvents(logs);

  //console.log('Timeline Events:', JSON.stringify(timelineEvents, null, 2));


  // Filter timeline events based on showDetails and showStackingEvents
  let filteredTimelineEvents: TimelineEvent[] = timelineEvents;

  if (!showDetails) {
    // If showDetails is false, only include KeyPressGroup and KeyPressInterrupt events
    filteredTimelineEvents = timelineEvents.filter(event => 
      event.type === 'KeyPressGroup' || event.type === 'KeyPressInterrupt'
    );
  } else if (!showStackingEvents) {
    // If showStackingEvents is false, exclude StackTimeout events
    filteredTimelineEvents = timelineEvents.filter(
      event => event.type !== 'StackTimeoutGroup'
    );
  }

  return (
    <div className="combo-display-wrapper">
      <div className="logs-container">
        {filteredTimelineEvents.map((timelineEvent, index) => {
          const showArrow = index < filteredTimelineEvents.length - 1;
          return (
            <div key={`timeline-${index}`} className="timeline-item">
              {timelineEvent.type === 'KeyPressGroup' ? (
                <KeyPressGroup
                  combinedGroup={timelineEvent.eventData}
                  spellImageUrls={spellImageUrls}
                  AutoAttackImage={autoAttackImageUrl}
                  showStackingEvents={showStackingEvents}
                  showStatEvents={showStatEvents}
                  showDetails={showDetails}
                  showCCEvents={showCCEvents}
                  championName={championName}
                  targetChampionName={targetChampionName}
                  targetMaxHealth={targetMaxHealth}
                />
              ) : timelineEvent.type === 'KeyPressInterrupt' ? (
                <KeyPressInterruptEvent
                  interruptEvent={timelineEvent.eventData}
                  spellImageUrls={spellImageUrls}
                  championName={championName}
                />
              ) : timelineEvent.type === 'StackTimeoutGroup' ? (
                <StackTimeoutGroup logs={timelineEvent.eventData} />
              ) : timelineEvent.type === 'TakeDownGroup' ? (
                <TakeDownGroupComponent 
                  group={timelineEvent.eventData} 
                  casterChampionName={championName}
                  showStackingEvents={showStackingEvents}
                />
              ) : null}
              {showArrow && (
                <div className="ability-arrow-wrapper">
                  <FontAwesomeIcon icon={faChevronRight} className="ability-arrow" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComboDisplay;